/**
 * Event add-ons (food, merch, extras sold alongside a ticket) — the pure,
 * client-safe rules. Data access lives in `addon-access.ts` / `addon-menu.ts`.
 */

/** A custom deadline replaces the preset, capped at the session start. */
export function addonCutoff(
  startTime: Date,
  cutoffHours: number,
  salesEndAt: Date | null,
) {
  const relative = startTime.getTime() - cutoffHours * 3_600_000;
  return new Date(
    salesEndAt ? Math.min(startTime.getTime(), salesEndAt.getTime()) : relative,
  );
}

export function addonIsOpen(
  startTime: Date,
  cutoffHours: number,
  salesEndAt: Date | null,
  now = new Date(),
) {
  return (
    now.getTime() < addonCutoff(startTime, cutoffHours, salesEndAt).getTime()
  );
}

/** How long before the deadline the one "last call" push goes out. */
export const ADDON_LAST_CALL_LEAD_HOURS = 6;

/**
 * The reminder card only appears in the final week before a deadline. Somebody
 * who books a month out is offered the menu at checkout; asking again on every
 * app open for four weeks would teach them to swat it without reading.
 */
export const ADDON_PROMPT_WINDOW_HOURS = 7 * 24;

/** One partner credit reads "by Loudmouth"; the organiser's own section has none. */
export function addonSectionByline(partnerName: string | null) {
  return partnerName?.trim() ? `by ${partnerName.trim()}` : null;
}

/** "Closes 24h before each session" — the organiser-side summary of a deadline. */
export function addonDeadlineRule(cutoffHours: number) {
  if (cutoffHours === 0) return "Open until each session starts";
  if (cutoffHours % 24 === 0) {
    const days = cutoffHours / 24;
    return `Closes ${days === 1 ? "1 day" : `${days} days`} before each session`;
  }
  return `Closes ${cutoffHours}h before each session`;
}

/** The section's call to action, or a neutral default that fits anything. */
export function addonActionLabel(label: string | null | undefined) {
  return label?.trim() || "See options";
}

/** "the Loudmouth counter" / "the Merch counter" — where a pickup happens. */
export function addonCounterLabel(section: {
  title: string;
  partnerName: string | null;
}) {
  return `the ${section.partnerName?.trim() || section.title} counter`;
}
